import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useLazyQuery, useApolloClient } from "@apollo/client";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Card, PageHeader, Button, Checkbox } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";
import DatePicker from "components/cck/DatePicker";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import ShipSelect from "view/ships/components/selects/ShipSelect";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import CharterSelect from "view/charters/components/selects/CharterSelect";

import { Language } from "imports/Utils";

import { DateUtils, FormattedDate } from "utils/DateUtils";

import InactivityTypeSelect from "./components/selects/InactivityTypeSelect";
import { getLocationTypeForInactivity } from "./utils/InactivityUtils";
import { inactivitySchema, InactivitySchemaInput, InactivitySchemaOutput } from "./utils/inactivity.schema";

import { CharterSubtype, CharterType, InactivityType } from "__generated__/enums";
import { SaveInactivityDocument } from "__generated__/view/inactivities/graphql/inactivities.mutations.generated";
import { GetInactivityCharterDocument, GetInactivityDocument } from "__generated__/view/inactivities/graphql/inactivities.queries.generated";

const InactivityCreateEdit = () => {

	const navigate = useNavigate();
	const confirm = useConfirm();
	const client = useApolloClient();
	const { id } = useParams();

	const [ isShowAllCharters, setIsShowAllCharters ] = useState(false);

	const editMode = Boolean(id);

	const [ charterVoyages, setCharterVoyages ] = useState<{
		id: number,
		start: number,
		end: number;
	}[]>([]);

	const { control, handleSubmit, reset, watch, setValue } = useForm<InactivitySchemaInput, unknown, InactivitySchemaOutput>({
		resolver: zodResolver(inactivitySchema),
		defaultValues: {
			type: InactivityType.Idle,
			ship: null,
			purchaseCharter: null,
			windowFrom: null,
			windowTo: null,
			location: null,
		},
	});

	const values = watch();

	const [ getInactivity ] = useLazyQuery(GetInactivityDocument, {
		onCompleted: response => {
			reset({
				type: response.inactivity?.type || InactivityType.Idle,
				ship: response.inactivity?.ship ?? null,
				purchaseCharter: response.inactivity?.purchaseCharters?.[0] ?? null,
				windowFrom: response.inactivity?.start ?? null,
				windowTo: response.inactivity?.end ?? null,
				location: response.inactivity?.calls?.[0]?.location ?? null,
			});
		},
	});

	const [ createOrEditInactivity, { loading } ] = useMutation(SaveInactivityDocument);

	const beforeSave = (data: InactivitySchemaOutput) => {
		if (overlappingWithVoyage) {
			confirm({
				type: "WARNING",
				title: intl.formatMessage({ id: "VOYAGE_OVERLAPPED" }),
			}).then(() => {
				save(data);
			});
		} else {
			save(data);
		}
	};

	const save = (data: InactivitySchemaOutput) => {
		createOrEditInactivity({
			variables: {
				id: id ? Number(id) : undefined,
				input: {
					shipId: data.ship.id,
					timePurchaseCharterId: data.purchaseCharter.id,
					windowFrom: data.windowFrom,
					windowTo: data.windowTo,
					baseLocationId: data.location.id,
					startLocationId: data.location.id,
					endLocationId: data.location.id,
				},
				type: data.type,
			},
		}).then(() => {
			navigate("/view/inactivities");
		});
	};

	const filterDateTime = (timeMillis: number, isStart: boolean, isDate: boolean) => {
		let min = (!isStart && values.windowFrom) ? values.windowFrom : values.purchaseCharter?.validFrom;
		const max = (isStart && values.windowTo) ? values.windowTo : values.purchaseCharter?.validUntil;
		if (isDate && !isStart) {
			min = moment(min).startOf("day").subtract(1, "second").valueOf();
		}
		return moment(timeMillis).isAfter(min) && moment(timeMillis).isBefore(max);
	};

	const overlappingWithVoyage = charterVoyages.length > 0 && !charterVoyages.every(v =>
		(moment(values.windowFrom).isBefore(v.start) && moment(values.windowTo).isBefore(v.start)) ||
		(moment(values.windowFrom).isAfter(v.end) && moment(values.windowTo).isAfter(v.end)),
	);

	useEffect(() => {
		if (values.purchaseCharter?.id) {
			client.query({
				query: GetInactivityCharterDocument,
				variables: {
					id: values.purchaseCharter.id,
				},
			}).then((response) => {
				const charter = response.data.charter;

				if (charter.__typename === "PurchaseCharter" && charter.subtype === CharterSubtype.Time) {
					const voyages = charter.voyages || [];
					setCharterVoyages(voyages.filter(v => v.id != Number(id)).map(v => ({ id: v.id, start: v.start || 0, end: v.end || 0 })));
				}
			});
		}
	}, [ values.purchaseCharter?.id ]);

	useEffect(() => {
		if (id) {
			getInactivity({
				variables: {
					id: Number(id),
				},
			});
		}
	}, []);

	return (
		<form onSubmit={handleSubmit(beforeSave)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("INACTIVITY")}
					<Helmet title={intl.formatMessage({ id: "INACTIVITY" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/inactivities")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} disabled={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={6}>
					<Card>
						<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="type"
								control={control}
								render={({ field }) => (
									<InactivityTypeSelect
										{...field}
										onChange={(option) => {
											field.onChange(option);
											setValue("location", null);
										}}
										disabled={editMode}
										clearable={false} />
								)} />
						</FormGroup>

						<FormGroup
							label={Language("SHIP")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="ship"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<ShipSelect
											{...field}
											disabled={editMode}
											onChange={(option) => {
												field.onChange(option);
												setValue("purchaseCharter", null);
												setValue("windowFrom", null);
												setValue("windowTo", null);
											}} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("CHARTER")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="purchaseCharter"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<CharterSelect
											disabled={!values.ship}
											search={{
												hasContract: true,
												charterType: CharterType.Purchase,
												charterSubtypes: [ CharterSubtype.Time ],
												ship: values.ship ?? undefined,
												validFrom: !isShowAllCharters ? moment().valueOf() : undefined,
											}}
											onChange={(option) => {
												field.onChange(option);
												setValue("windowFrom", null);
												setValue("windowTo", null);
											}}
											value={field.value}
											withoutCreate />

										<Checkbox
											label={intl.formatMessage({ id: "SHOW_ALL_CHARTERS" })}
											checked={isShowAllCharters}
											onChange={(e) => setIsShowAllCharters(e.target.checked)}
											size="small" />

										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						{(values.purchaseCharter?.validFrom && values.purchaseCharter.validUntil) && (
							<React.Fragment>
								<FormGroup label={Language("VALID_FROM")} labelPlacement="left-center" overflow="hidden">
									<FormattedDate date={values.purchaseCharter.validFrom} />
								</FormGroup>

								<FormGroup label={Language("VALID_UNTIL")} labelPlacement="left-center" overflow="hidden">
									<FormattedDate date={values.purchaseCharter.validUntil} />
								</FormGroup>
							</React.Fragment>
						)}

						<FormGroup
							label={Language("START_END_WINDOW")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Grid container spacing={3}>
								<Grid size={6}>
									<Controller
										name="windowFrom"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<DatePicker
													{...field}
													filterDate={date => filterDateTime(moment(date).unix() * 1000, true, true)}
													filterTime={date => filterDateTime(moment(date).unix() * 1000, true, false)}
													disabled={!values.purchaseCharter}
													minDate={DateUtils.getDateOrUndefined(values.purchaseCharter?.validFrom)}
													maxDate={DateUtils.getDateOrUndefined(values.purchaseCharter?.validUntil)}
													showTimeInput />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</Grid>

								<Grid size={6}>
									<Controller
										name="windowTo"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<DatePicker
													{...field}
													filterDate={date => filterDateTime(moment(date).unix() * 1000, false, true)}
													filterTime={date => filterDateTime(moment(date).unix() * 1000, false, false)}
													disabled={!values.purchaseCharter}
													minDate={DateUtils.getDateOrUndefined(values.purchaseCharter?.validFrom)}
													maxDate={DateUtils.getDateOrUndefined(values.purchaseCharter?.validUntil)}
													showTimeInput />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</Grid>
							</Grid>
						</FormGroup>

						<FormGroup
							label={Language("INACTIVITY_LOCATION")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="location"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<LocationSelect
											{...field}
											types={[ getLocationTypeForInactivity(values.type) ]} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default InactivityCreateEdit;
